import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

interface Options extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

/**
 * This hook replace an older in-house hook that was not tested.
 *
 * This React Hook detects visibility of a component on the viewport using the
 * `IntersectionObserver` API natively present in the browser.
 *
 * It can be very useful to lazy-loading of images, implementing "infinite
 * scrolling" or starting animations for example.
 *
 * It takes optionally `root`, `rootMargin` and `threshold` arguments from the
 * native IntersectionObserver API and `freezeOnceVisible` to only catch the
 * first appearance too.
 *
 * Source at https://usehooks-ts.com/react-hook/use-intersection-observer
 */
export function useIntersectionObserver(
  /** From `useRef()` */
  elementRef: RefObject<Element>,
  {
    threshold = 0,
    root = null,
    rootMargin = '0%',
    freezeOnceVisible = false,
  }: Options
): IntersectionObserverEntry | undefined {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = ([newEntry]: IntersectionObserverEntry[]): void => {
    setEntry(newEntry);
  };

  useEffect(() => {
    const node = elementRef?.current; // DOM Ref
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !node) {
      return;
    }

    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);

    observer.observe(node);

    return () => observer.disconnect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, JSON.stringify(threshold), root, rootMargin, frozen]);

  return entry;
}
